import { excludedPermissionKeysForSuperAdmin, excludedPermissionKeysForTenant } from '../constants/permission'
import { checkIsTenant } from './constants'

// Generic function to check if a key is enabled
export const isKeyEnabled = (data, targetKey) => {
  for (const section in data) {
    if (data[section].subModules) {
      const subModule = data[section].subModules.find(
        (module) => module.key === targetKey
      )
      if (subModule) {
        return subModule.enabled // Return true or false
      }
    }
  }
  return false // Default to false if the key is not found
}

/**
 * Filters the input data to return only the enabled modules and their enabled submodules.
 *
 * @param {Object} data - An object where each key represents a module and its value contains module details.
 * @returns {Object} - A new object containing only the enabled modules and their enabled submodules.
 */
export function filterEnabledModules (formValue) {
  const data = formValue.subModulePermissions
  const result = {}

  for (const moduleKey in data) {
    const module = data[moduleKey]

    if (module.enabled) {
      // Filter submodules that are enabled
      let enabledSubModules = module.subModules.filter(subModule => subModule.enabled)

      // If role is 'Support', remove submodules with permission 'Admins'
      if (formValue?.role === 'Support') {
        enabledSubModules = enabledSubModules.filter(subModule => subModule.permission !== 'Admins')
      }

      // Add the module with its enabled submodules to the result
      result[moduleKey] = {
        ...module,
        subModules: enabledSubModules
      }
    }
  }

  return result
}

/**
   * Sorts a permissions object by the `moduleOrder` property of its entries.
   *
   * @param {Object} permissions - The permissions object to be sorted.
   * @returns {Object} - A new sorted permissions object, or the original input if invalid.
   */
export const sortPermissions = (permissions) => {
  if (!permissions || typeof permissions !== 'object' || Array.isArray(permissions)) {
    return permissions // Return the original input to avoid breaking the flow
  }

  return Object.entries(permissions)
    .sort(([, a], [, b]) => a.moduleOrder - b.moduleOrder) // Sort by moduleOrder only
    .reduce((sorted, [key, value]) => {
      sorted[key] = { ...value } // Preserve subModules as is
      return sorted
    }, {})
}

/**
 * Updates user permissions based on the main permissions configuration.
 *
 * - If a module is disabled, all its submodule permissions are removed.
 * - If a module is enabled, missing submodule permissions are added with default values.
 * - Special handling for `casinoManagement`, `antiMoneyLaundering`, and `responsibleGambling` modules.
 *
 * @param {Object} mainPermissions - The main permissions structure with module and submodule settings.
 * @param {Object} userPermissions - The existing user permissions that need to be updated.
 * @returns {Object} The updated user permissions object.
 */
export const updateUserPermissions = (mainPermissions, userPermissions) => {
  if (!mainPermissions || !userPermissions) {
    return {}
  }

  let updatedPermissions = { ...userPermissions }

  if (!Object.isExtensible(userPermissions)) {
    updatedPermissions = JSON.parse(JSON.stringify(userPermissions))
  }

  for (const moduleKey in mainPermissions) {
    const module = mainPermissions[moduleKey]

    if (module.enabled === false) {
      // If any module is disabled from group permission module, remove all permissions from user permission module for that.
      if (module.subModules) {
        for (const subModule of module.subModules) {
          if (updatedPermissions[subModule.permission]) {
            delete updatedPermissions[subModule.permission]
          }
        }
      }

      if (moduleKey === 'casinoManagement' && updatedPermissions.CasinoManagement) {
        // If the 'Casino Management' module is disabled from the group permissions, remove the 'CasinoManagement' permission from  user permission module also.
        // This ensures that API calls dependent on this permission are restricted accordingly.
        delete updatedPermissions.CasinoManagement
      }

      if (moduleKey === 'antiMoneyLaundering' && updatedPermissions.AML) {
        // If the 'Anti Money Laundering' module is disabled from the group permissions, remove the 'AML' permission from  user permission module also.
        // This ensures that AML-related API endpoints cannot be accessed without proper authorization.
        delete updatedPermissions.AML
      }

      if (moduleKey === 'responsibleGambling' && updatedPermissions.RG) {
        // If the 'Responsible Gambling' module is disabled from the group permissions module, remove the 'RG' permission from  user permission module.
        // This prevents unauthorized access to Responsible Gambling-related functionalities.
        delete updatedPermissions.RG
      }
    } else {
      if (module.subModules) {
        for (const subModule of module.subModules) {
          if (subModule.enabled === false) {
            // If any sub-module is disabled from group permission module, remove permissions from user permission module for that.
            if (updatedPermissions[subModule.permission]) {
              delete updatedPermissions[subModule.permission]
            }
          } else {
            // If sub-module is enabled from group permission module, ensure permission exists or add it in user permission module with default permissions
            if (!updatedPermissions[subModule.permission]) {
              // If permission doesn't exist in user permission module, create it and add read permission
              updatedPermissions[subModule.permission] = ['R']
            } else if (!updatedPermissions[subModule.permission].includes('R')) {
              // If permission exists, check if read permission is added in user module permission
              updatedPermissions[subModule.permission] = [...updatedPermissions[subModule.permission], 'R']
            }
          }
        }
      }

      if (moduleKey === 'casinoManagement') {
        // If the 'Casino Management' module is enabled in the  group permissions module, ensure 'CasinoManagement' permission is granted in permission.
        // This is required because some API endpoints depend on this permission to function correctly.
        if (!updatedPermissions.CasinoManagement) {
          updatedPermissions.CasinoManagement = ['C', 'R', 'U', 'T', 'D']
        } else {
          ['C', 'R', 'U', 'T', 'D'].forEach(p => {
            if (!updatedPermissions.CasinoManagement.includes(p)) {
              updatedPermissions.CasinoManagement = [...updatedPermissions.CasinoManagement, p]
            }
          })
        }
      }
      if (moduleKey === 'antiMoneyLaundering') {
        // If the 'Anti Money Laundering' module is enabled in the group permissions, ensure 'AML' permission is granted.
        // This is required because some API endpoints depend on this permission to function correctly.
        if (!updatedPermissions.AML) {
          updatedPermissions.AML = ['R', 'U']
        } else {
          ['R', 'U'].forEach(p => {
            if (!updatedPermissions.AML.includes(p)) {
              updatedPermissions.AML = [...updatedPermissions.AML, p]
            }
          })
        }
      }
      if (moduleKey === 'responsibleGambling') {
        // If the 'Responsible Gambling' module is enabled in the group, ensure 'RG' permission is granted.
        // This is required because some API endpoints depend on this permission to function correctly.
        if (!updatedPermissions.RG) {
          updatedPermissions.RG = ['R', 'U']
        } else {
          ['R', 'U'].forEach(p => {
            if (!updatedPermissions.RG.includes(p)) {
              updatedPermissions.RG = [...updatedPermissions.RG, p]
            }
          })
        }
      }
    }
  }

  return updatedPermissions
}

export const updateSubModuleEnabled = (modules, subModuleKey, enabledValue) => {
  // Create a new copy of the modules object
  const updatedModules = Object.keys(modules).reduce((acc, moduleKey) => {
    const module = modules[moduleKey]

    // Create a shallow copy of the module
    const updatedModule = { ...module }

    if (module.subModules) {
      // Create a new array with updated subModules
      updatedModule.subModules = module.subModules.map(subModule => {
        if (subModule.permission === subModuleKey) {
          return { ...subModule, enabled: enabledValue } // Update enabled value
        }
        return subModule
      })

      // If any sub-module is enabled, ensure the parent module is enabled
      updatedModule.enabled = updatedModule.subModules.some(sub => sub.enabled)
    }

    acc[moduleKey] = updatedModule
    return acc
  }, {})

  return updatedModules
}

export const updateAdminPermissions = (adminPermissions, groupPermissions) => {
  if (
    !adminPermissions || typeof adminPermissions !== 'object' ||
    !groupPermissions || typeof groupPermissions !== 'object' ||
    Object.keys(groupPermissions).length === 0
  ) {
    return adminPermissions
  }

  const updatedAdminPermissions = JSON.parse(JSON.stringify(adminPermissions))

  for (const moduleKey in updatedAdminPermissions) {
    const adminModule = updatedAdminPermissions[moduleKey]
    const groupModule = groupPermissions[moduleKey]

    // If the group module is explicitly disabled, disable all submodules in adminPermissions
    if (!groupModule?.enabled) {
      if (adminModule.subModules && Array.isArray(adminModule.subModules)) {
        adminModule.subModules = adminModule.subModules.map(subModule => ({
          ...subModule,
          enabled: false
        }))
      }
      adminModule.enabled = false
    } else if (adminModule.subModules && Array.isArray(adminModule.subModules)) {
      if (groupModule.subModules && Array.isArray(groupModule.subModules)) {
        // Update subModules based on groupPermissions
        adminModule.subModules = adminModule.subModules.map(adminSubModule => {
          const matchingGroupSubModule = groupModule.subModules.find(
            groupSub => groupSub.key === adminSubModule.key
          )
          if (matchingGroupSubModule) {
            return { ...adminSubModule, enabled: matchingGroupSubModule.enabled }
          } else {
            // if group sub module is not present then disable the admin submodule
            return { ...adminSubModule, enabled: false }
          }
        })
      } else {
        // if group does not have submodules, disable all admin submodules.
        adminModule.subModules = adminModule.subModules.map(subModule => ({
          ...subModule,
          enabled: false
        }))
      }

      // Update the main module enabled status based on its submodules
      adminModule.enabled = adminModule.subModules.some(sub => sub.enabled)
    } else {
      if (groupModule.subModules == null) {
        adminModule.enabled = groupModule.enabled
      }
    }
  }

  return updatedAdminPermissions
}

export const permissionKeyLabel = (label) => {
  const labelMap = {
    CMS: 'CMS',
    Bonus: 'Bonus',
    Users: 'Players',
    Admins: 'Staff',
    Tenant: 'Tenant Management',
    Themes: 'Themes',
    Reviews: 'Review Management',
    KycLabel: 'KYC Settings',
    Language: 'Languages',
    Settings: 'Other Settings',
    KpiReport: 'KpiReport',
    RGFlagged: 'RG Flagged Players',
    AMLFlagged: 'AML Flagged Players',
    CasinoGame: 'Games',
    Currencies: 'Currencies',
    GameReport: 'Game Report',
    RGExcluded: 'Self Excluded Players',
    AurumReport: 'Aurum Report',
    BannedGames: 'Banned Games',
    DisableUser: 'DisableUser',
    RGThreshold: 'RG Set Threshold',
    UserComment: 'User Comment',
    AMLThreshold: 'AML Set Threshold',
    BannedPlayer: 'Banned Player',
    ImageGallery: 'Image Gallery',
    Transactions: 'Financial Transactions',
    EmailTemplate: 'Email Template',
    MultiLanguage: 'Translations',
    CasinoProvider: 'Game Providers',
    TenantSettings: 'Settings',
    DemographReport: 'DemographReport',
    ErrorManagement: 'Error Management',
    GroupPermission: 'Group Management',
    BannerManagement: 'Banner Management',
    CasinoAggregator: 'Game Aggregators',
    CasinoManagement: 'Casino Management',
    DepositGgrReport: 'DepositGgrReport',
    KpiSummaryReport: 'Kpi Summary Report',
    LivePlayerReport: 'Live Player Report',
    WageringTemplate: 'Wagering Template',
    CashierManagement: 'Cashier Methods',
    CasinoAllCategory: 'Game Categories',
    CasinoTransaction: 'Casino Transactions',
    LoyaltyManagement: 'Loyalty Management',
    RegistrationField: 'Registration',
    RestrictedCountry: 'Countries',
    TenantCredentials: 'Tenant Credentials',
    TenantMultiLicense: 'Tenant MultiLicense',
    CasinoAllSubCategory: 'Game Sub Categories',
    TenantConfigurations: 'Tenant Configurations',
    PlayerLiabilityReport: 'Player Liability Report',
    PlayerManagementReport: 'Player Management Report',
    BannedPlayerSetting: 'Banned Player Setting',
    HomeUIManagement: 'Home Page',
    AccountUIManagement: 'Account Page'
  }

  return labelMap[label] || label // If label exists in labelMap, return its value; otherwise, return the label itself.
}

export const filterAdminSubModules = (moduleModules, values) => {
  if (!moduleModules || values?.role !== 'Support') {
    return moduleModules || {}
  }

  // Create a new object with filtered submodules across all modules
  const filteredModules = Object.keys(moduleModules).reduce((acc, moduleKey) => {
    const module = moduleModules[moduleKey]

    if (module?.subModules) {
      acc[moduleKey] = {
        ...module,
        subModules: module.subModules.filter(subModule => subModule.permission !== 'Admins')
      }
    } else {
      acc[moduleKey] = module // Keep modules without subModules as is
    }

    return acc
  }, {})

  return filteredModules
}

export const filterProfilePermissions = (permissions) => {
  const isTenant = checkIsTenant()

  return Object.keys(permissions || {}).reduce((acc, key) => {
    // If the user is a tenant, exclude permissions listed in excludedPermissionKeysForTenant
    // If the user is NOT a tenant, exclude permissions listed in excludedPermissionKeysForSuperAdmin
    if (
      (isTenant && excludedPermissionKeysForTenant.includes(key)) ||
      (!isTenant && excludedPermissionKeysForSuperAdmin.includes(key))
    ) {
      return acc
    }

    acc[key] = permissions[key]
    return acc
  }, {})
}
